import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, ComposedChart,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import { CircularProgress } from '../CircularProgress';
import { useFormatters } from '../../../contexts/PreferencesContext';
import type { SalesOrdersDashboardData } from '../../../lib/api/sales-orders-dashboard';
import type { OpenDrillDown } from './SalesOrdersShell';

/** Trend window size — the chart shows this many weeks at once. */
const WINDOW_SIZE = 12;

/** Format a date range from two ISO dates using the locale-aware formatter. */
function formatRange(
  startIso: string | undefined,
  endIso: string | undefined,
  formatDate: (d: string) => string,
): string {
  if (!startIso || !endIso) return '';
  return `${formatDate(startIso)} – ${formatDate(endIso)}`;
}

const PALETTE = {
  otif: '#0f766e',
  otd:  '#0284c7',
  spend: '#2563eb',
  open: '#cbd5e1',
  target: '#94a3b8',
};

const TARGET_PCT = 95;

interface Props {
  data: SalesOrdersDashboardData;
  formatCurrency: (n: number) => string;
  openDrillDown: OpenDrillDown;
  /** Feature-flag gate — when false, OTIF tile/trend line is not rendered. */
  showOtif: boolean;
  /** Supplier-portal install dispatches goods — relabel "Received" → "Delivered". */
  isSupplierPortalMode: boolean;
  /**
   * Customer (counterparty / customer-side) viewers see ONLY the top KPI cards —
   * the 12-week trend charts below are hidden. Supplier/admin viewers keep all.
   */
  isCustomer: boolean;
}

/** Per-week panel: Order Status snapshot at top + 12-week Delivery / Cost trends. */
export function PerWeek({ data, formatCurrency, openDrillDown, showOtif, isSupplierPortalMode, isCustomer }: Props) {
  const deliveredLabel = isSupplierPortalMode ? 'Delivered' : 'Received';
  const { formatDate } = useFormatters();
  const allWeeks = data.per_week.weeks;

  // Trend windowing: the backend returns more history than is shown at once
  // (oldest → newest). `windowOffset` counts weeks back from the most recent
  // window: 0 = most recent 12 weeks, increasing pages further into the past.
  // The point-in-time Order Status / Delivery / Cost KPI tiles always reflect
  // the most recent 12 weeks — only the trend charts page.
  const maxOffset = Math.max(0, allWeeks.length - WINDOW_SIZE);
  const [windowOffset, setWindowOffset] = useState(0);
  const clampedOffset = Math.min(windowOffset, maxOffset);

  // KPI tiles stay on the most recent 12 weeks regardless of the visible window.
  const kpiWeeks = useMemo(
    () => allWeeks.slice(Math.max(0, allWeeks.length - WINDOW_SIZE)),
    [allWeeks],
  );

  // The visible trend window pages through history with the nav arrows.
  const weeks = useMemo(() => {
    const end = allWeeks.length - clampedOffset;
    const start = Math.max(0, end - WINDOW_SIZE);
    return allWeeks.slice(start, end);
  }, [allWeeks, clampedOffset]);

  const atNewest = clampedOffset <= 0;
  const atOldest = clampedOffset >= maxOffset;
  const windowRange = formatRange(weeks[0]?.week_start, weeks[weeks.length - 1]?.week_end, formatDate);

  const trend = weeks.map((w) => ({
    week: w.label,
    week_start: w.week_start,
    week_end: w.week_end,
    otif: w.delivery.otif_pct,
    otd: w.delivery.otd_pct,
    received: w.cost.received_value,
    // Outstanding = LIVE outstanding value of lines due that week
    // (open_value + arrears_value). Excludes stale closed lines received short.
    open: w.cost.open_value + w.cost.arrears_value,
    target: TARGET_PCT,
  }));

  // KPI tiles summarise the most recent 12 weeks (point-in-time), independent
  // of the trend window the user has paged to.
  const totalReceived  = kpiWeeks.reduce((a, w) => a + w.cost.received_value, 0);
  const totalCommitted = kpiWeeks.reduce((a, w) => a + w.cost.total_value, 0);
  // Outstanding in the window = LIVE outstanding value of lines due in the 12
  // weeks (open_value + arrears_value per week). Excludes stale closed lines
  // received short; the live open book is the separate "Portfolio Open" tile.
  const totalOutstanding = kpiWeeks.reduce((a, w) => a + w.cost.open_value + w.cost.arrears_value, 0);
  const lastKpiWeek = kpiWeeks[kpiWeeks.length - 1];
  const lastOtif = lastKpiWeek?.delivery.otif_pct ?? 0;
  const lastOtd = lastKpiWeek?.delivery.otd_pct ?? 0;

  // 12-week period bounds, used for drill-downs from KPI tiles (most recent 12).
  const period12 = kpiWeeks.length
    ? {
        start: kpiWeeks[0].week_start,
        end: kpiWeeks[kpiWeeks.length - 1].week_end,
        label: '12-week window',
      }
    : null;

  // ---- Trend / bar chart click handlers ----
  const handleWeekPointClick = (
    weekStart: string | undefined,
    weekEnd: string | undefined,
    label: string | undefined,
  ) => {
    if (!weekStart || !weekEnd) return;
    openDrillDown({
      scope: 'delivered',
      period: { start: weekStart, end: weekEnd, label: label || 'week' },
      title: `${deliveredLabel} — ${label || 'selected week'}`,
      subtitle: 'Delivered — due that week',
    });
  };

  const handleWeekBarClick = (
    weekStart: string | undefined,
    weekEnd: string | undefined,
    label: string | undefined,
    seriesKey: 'received' | 'open',
  ) => {
    if (!weekStart || !weekEnd) return;
    if (seriesKey === 'received') {
      openDrillDown({
        scope: 'delivered',
        period: { start: weekStart, end: weekEnd, label: label || 'week' },
        title: `${deliveredLabel} Value — ${label || 'selected week'}`,
        subtitle: 'Lines due that week that were received',
      });
    } else {
      openDrillDown({
        scope: 'outstanding',
        period: { start: weekStart, end: weekEnd, label: label || 'week' },
        title: `Outstanding — ${label || 'selected week'}`,
        subtitle: 'Lines due that week not yet fully delivered',
      });
    }
  };

  // Prev/Next paging controls for the trend window. "Next" disabled at the
  // most-recent window, "Prev" at the oldest. Shows the visible date range.
  const windowNav = maxOffset > 0 ? (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className="text-[11px] tabular-nums text-slate-500 mr-1">{windowRange}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        aria-label="Earlier weeks"
        disabled={atOldest}
        onClick={() => setWindowOffset((o) => Math.min(maxOffset, o + WINDOW_SIZE))}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-7 p-0"
        aria-label="Later weeks"
        disabled={atNewest}
        onClick={() => setWindowOffset((o) => Math.max(0, o - WINDOW_SIZE))}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Order Status" accent="red" subtitle="point-in-time snapshot">
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label="Open Lines (total)"
              value={<span className="text-2xl font-semibold tabular-nums">{(data.order_status.open_count ?? 0).toLocaleString()}</span>}
              foot="all open, incl. overdue"
              onClick={() => openDrillDown({
                scope: 'open_all',
                period: null,
                title: 'Open Lines (total)',
                subtitle: 'Whole open book — pending/partial, incl. overdue',
              })}
            />
            <KPICell
              label="On Hold"
              value={<span className="text-2xl font-semibold tabular-nums">{data.order_status.on_hold_count.toLocaleString()}</span>}
              foot="held PO lines"
              onClick={() => openDrillDown({
                scope: 'on_hold',
                period: null,
                title: 'On-Hold PO Lines',
                subtitle: 'Currently held',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle="last 12 weeks">
          <div className={`grid ${showOtif ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
            {showOtif && (
              <KPICell
                label="OTIF (latest)"
                value={<CircularProgress percentage={lastOtif} size={68} strokeWidth={6} color={performanceColor(lastOtif)} />}
              />
            )}
            <KPICell
              label="OTD (latest)"
              value={<CircularProgress percentage={lastOtd} size={68} strokeWidth={6} color={performanceColor(lastOtd)} />}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="last 12 weeks">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label={`${deliveredLabel} (12wk)`}
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(totalReceived)}</span>}
              foot={`of ${formatCurrency(totalCommitted)} due`}
              onClick={() => openDrillDown({
                scope: 'delivered',
                period: period12,
                title: `${deliveredLabel} — Last 12 weeks`,
                subtitle: 'Lines due in the window that were received',
              })}
            />
            <KPICell
              label="Outstanding"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(totalOutstanding)}</span>}
              foot="committed, not delivered (12wk)"
            />
            <KPICell
              label="Portfolio Open"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.open_orders_value)}</span>}
              foot="all forward (now)"
              onClick={() => openDrillDown({
                scope: 'open',
                period: null,
                title: 'Portfolio — All Open Lines',
                subtitle: 'Every open line, all forward periods',
              })}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Customer (counterparty) viewers see ONLY the top KPI cards above — the
          trend charts below are supplier/admin-only. */}
      {!isCustomer && (
      <>
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-sm font-medium text-slate-700">{showOtif ? 'OTIF & OTD' : 'OTD'} — 12-Week Trend</CardTitle>
              <p className="text-[11px] text-muted-foreground">Target {TARGET_PCT}% (dashed) • click a point to drill in</p>
            </div>
            {windowNav}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} unit="%" />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => `${Math.round(v)}%`} />
                <ReferenceLine y={TARGET_PCT} stroke={PALETTE.target} strokeDasharray="4 4" />
                {showOtif && (
                  <Line type="monotone" dataKey="otif" stroke={PALETTE.otif} strokeWidth={2.5} dot={{ r: 3 }} name="OTIF %"
                    activeDot={{
                      r: 5,
                      style: { cursor: 'pointer' },
                      onClick: (_e: unknown, payload: unknown) => {
                        const p = payload as { payload?: { week_start?: string; week_end?: string; week?: string } };
                        handleWeekPointClick(p?.payload?.week_start, p?.payload?.week_end, p?.payload?.week);
                      },
                    }}
                  />
                )}
                <Line type="monotone" dataKey="otd"  stroke={PALETTE.otd}  strokeWidth={2.5} dot={{ r: 3 }} name="OTD %"
                  activeDot={{
                    r: 5,
                    style: { cursor: 'pointer' },
                    onClick: (_e: unknown, payload: unknown) => {
                      const p = payload as { payload?: { week_start?: string; week_end?: string; week?: string } };
                      handleWeekPointClick(p?.payload?.week_start, p?.payload?.week_end, p?.payload?.week);
                    },
                  }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="text-sm font-medium text-slate-700">Weekly Order-Line Value — {deliveredLabel} vs Outstanding</CardTitle>
              <p className="text-[11px] text-muted-foreground">PO lines due that week, split by what's been delivered vs still outstanding</p>
            </div>
            {windowNav}
          </div>
        </CardHeader>
        <CardContent>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="week" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => v >= 1000000 ? `£${(v/1000000).toFixed(1)}M` : v >= 1000 ? `£${(v/1000).toFixed(0)}k` : `£${v}`} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} formatter={(v: number) => formatCurrency(v)} />
                <Bar dataKey="received" stackId="cost" fill={PALETTE.spend} radius={[0, 0, 0, 0]} name={deliveredLabel}
                  onClick={(d) => { const p = d?.payload as { week_start?: string; week_end?: string; week?: string } | undefined; handleWeekBarClick(p?.week_start, p?.week_end, p?.week, 'received'); }}
                  style={{ cursor: 'pointer' }} />
                <Bar dataKey="open"     stackId="cost" fill={PALETTE.open}  radius={[3, 3, 0, 0]} name="Outstanding"
                  onClick={(d) => { const p = d?.payload as { week_start?: string; week_end?: string; week?: string } | undefined; handleWeekBarClick(p?.week_start, p?.week_end, p?.week, 'open'); }}
                  style={{ cursor: 'pointer' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
